import * as Sentry from '@sentry/browser';
import { ILogger } from '@/Contracts';
import { version } from '../../../package.json';
/**
 * Initialize Sentry with the provided configuration
 * @param options Configuration options for Sentry
 */
export const initSentry = (options?: {
    dsn?: string;
    environment?: string;
    release?: string;
    debug?: boolean;
    tracesSampleRate?: number;
}) => {
    const {
        dsn = import.meta.env.VITE_SENTRY_DSN,
        environment = import.meta.env.VITE_ENVIRONMENT || 'development',
        release = version,
        debug = import.meta.env.DEV,
        tracesSampleRate = 1.0,
    } = options || {};

    // Only initialize when a DSN is configured. Without one we skip init outside production
    // (so Sentry doesn't warn "No DSN provided" in dev); in production a missing DSN is fatal.
    if (!dsn) {
        if (!import.meta.env.DEV) {
            throw new Error('Failed to initialise sentry. Missing dsn');
        }
        return;
    }

    Sentry.init({
        dsn,
        environment,
        release,
        debug,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate,
    });
};

/**
 * Log an error to Sentry
 * @param error The error to log
 * @param context Additional context information
 */
export const logError = (error: Error) => {
    // Always log to console in development
    if (import.meta.env.DEV) {
        console.error(error);
    }
    // Log to Sentry
    return Sentry.captureException(error);
};
// The Sentry-backed logger is the production logger: it reports errors and ignores debug logs.
const logDebug = () => {};

initSentry();
export const SentryService: ILogger = {
    logError,
    logDebug,
};
